import React from 'react';
import get from 'lodash/get';
import {uniqBy} from 'lodash';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { Row, Col, Layout, Progress, Select, Icon, DatePicker, Table } from 'antd';
import { Bar, HorizontalBar } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';

import AdminHeader from '../../components/AdminHeader/AdminHeader';
import Footer from '../../components/Footer/Footer';
import classes from './AdminDashboard.css';

import { logout } from "../../reducers/user/actions";
import isAuthenticated from '../../utils/isAuthenticated';
import { getDashboardRequest, getSitesRequest, getFollowUpsRequest } from '../../api/requests';
import { withParams } from '../../utils/url';
import {
  getDashboardPermission,
  getDashboardEnrolmentStagesPermission,
  getDashboardFollowUp90dPermission,
  getDashboardReadDeviationPermission,
  getDashboardReadSAEPermission,
  getDashboardReadEnrolmentBySitePermission,
  getUserTimezone,
  getUserShortTimezone
} from "../../reducers/user/selectors";

const routes = require('../../routes.json');

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;

class AdminDashboard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      dashboard: {},
      filter: '',
      filterSiteId: null,
      site: '',
      sites: [],
      dateRange: [moment(), moment().add(30, 'days')],
      followup_filter: 'follow_ups_due',
      followups: [],
      currentPage: 1,
      total: 1,
      pageSize: 25,
      isLoadingFollowUps: false,
    };
  }

  async componentDidMount() {
    this.getDashboardData();
    this.getFollowUps();

    try {
      const { data } = await getSitesRequest();
      this.setState({ sites: data });
    } catch (e) { if (get(e, 'response.status') === 401) { this.props.logout();  return; } console.log(e);}

  }

  columns = [
    {
      dataIndex: "site",
      key: "site",
      title: "Site",
      render: val => (<span>{get(val, 'code')} - {get(val, 'name')}</span>)
    },
    {
      dataIndex: "subject",
      key: "subject",
      title: "Subject",
      render: val => (<span>{get(val, 'code')}</span>)
    },
    {
      dataIndex: "created",
      key: "created",
      title: "Created",
      render: (val, record) => (<span>{moment.utc(val).tz(this.props.userTimezone || get(record, 'site.time_zone') || 'UTC').format('YYYY-MM-DD HH:mm')} {this.props.userShortTimezone || get(record, 'site.short_tz') || 'UTC'}</span>)
    },
    {
      dataIndex: "scheduled_date",
      key: "scheduled_date",
      title: "Scheduled date",
      render: (val, record) => (<span>{moment.utc(val).tz(this.props.userTimezone || get(record, 'site.time_zone') || 'UTC').format('YYYY-MM-DD HH:mm')} {this.props.userShortTimezone || get(record, 'site.short_tz') || 'UTC'}</span>)
    },
    {
      dataIndex: "subject",
      key: "subject_action",
      title: "Actions",
      render: val => (
        <div>
          <Link to={withParams(routes.admin.followUp90DaySingle, { subjectID: get(val, 'code') })}><Icon type="edit" theme="filled" className={classes.viewIcon} /></Link>
        </div>
      )
    },
  ];

  handleFilterChange = (value) => {
    if (value === 'month') { this.setState({ dateRange: [moment(), moment().add(30, 'days')] }) }

    this.setState({ filter: value, site: null }, this.getDashboardData);
  };

  handleSiteChange = (value) => {
    this.setState({ site: value }, this.getDashboardData);
  };

  handleSiteFollowUpFilterChange = (value) => {
    this.setState({ filterSiteId: value }, this.getFollowUps);
  };

  getDashboardData = async () => {
    this.setState({ isLoadingFollowUps: true });
    try {
      let dateRangeParams = {};

      if (this.state.filter === 'month') {
        const { dateRange } = this.state;
        dateRangeParams = {
          // start_date: moment.utc(dateRange[0]).tz(this.props.userTimezone).format('YYYY-MM-DDT00:00'),
          start_date: moment(moment(dateRange[0]).format('YYYY-MM-DDT00:00')).toISOString(),
          end_date: moment(moment(dateRange[1]).add(1, 'day').format('YYYY-MM-DDT00:00')).toISOString()
        }
      }

      const { data } = await getDashboardRequest({ filter: this.state.filter, siteCode: this.state.site, ...dateRangeParams });

      this.setState({ dashboard: data });
    } catch (e) { if (get(e, 'response.status') === 401) { this.props.logout();  return; } console.log(e);}

    this.setState({ isLoadingFollowUps: false });
  };

  getFollowUps = async (page = 1, per_page = this.state.pageSize) => {
    try {
      const { data } = await getFollowUpsRequest({ filter_by: this.state.followup_filter, site_id: this.state.filterSiteId, page, per_page });
      this.setState({ followups: get(data, 'data'), total: get(data, 'total_pages') * per_page, currentPage: page });
    } catch (e) { if (get(e, 'response.status') === 401) { this.props.logout();  return; } console.log(e);}
  };

  setFollowUpFilter = filter => {
    this.setState({ followup_filter: filter, filterSiteId: null }, () => {
      if (filter !== 'site') {
        this.getFollowUps()
      }
    });
  };

  clearFollowUpFilter = filter => {
    this.setState({ followup_filter: 'follow_ups_due' });
  };

  handleTableChange = (pagination) => {
    this.setState({
      pageSize: pagination.pageSize,
    }, () => this.getFollowUps(pagination.current, pagination.pageSize));
  };

  getEnrolmentsLabels = () => {
    const { filter } = this.state;
    const enrolments = get(this.state.dashboard, 'enrolments');

    return Array.isArray(enrolments) ? enrolments.map(item => {
      if (filter === 'site') { return get(item, 'site'); }
      if (filter === 'month') { return get(item, 'year_month'); }
    }) : [this.state.site ? get(this.state.sites.find(item => item.code === this.state.site), 'name') : 'All sites'];
  };

  getEnrolmentsDatasets = () => {
    const dataSet = [{ label: "Total subjects", backgroundColor: "#3e95cd" }];
    const enrolments = get(this.state.dashboard, 'enrolments', []);

    if (Array.isArray(enrolments)) {
      dataSet[0].data = get(this.state.dashboard, 'enrolments', []).map(item => get(item, 'count'));
    } else {
      dataSet[0].data = [enrolments];
    }
    return dataSet;
  };

  getSaesLabels = () => {
    const { filter } = this.state;
    const sae = get(this.state.dashboard, 'sae');

    return Array.isArray(sae) ? sae.map(item => {
      if (filter === 'site') { return get(item, 'site'); }
      if (filter === 'month') { return get(item, 'year_month'); }
    }) : [this.state.site ? get(this.state.sites.find(item => item.code === this.state.site), 'name') : 'All sites'];
  };

  getSaesDatasets = () => {
    const dataSet = [{ label: "SAE", backgroundColor: "#f00" }];
    const sae = get(this.state.dashboard, 'sae', []);

    if (Array.isArray(sae)) {
      dataSet[0].data = get(this.state.dashboard, 'sae', []).map(item => get(item, 'count'));
    } else {
      dataSet[0].data = [sae];
    }
    return dataSet;
  };


  getDeviationsLabels = () => {
    const { filter } = this.state;
    const protocolDeviations = get(this.state.dashboard, 'protocol_deviations');

    return Array.isArray(protocolDeviations) ? protocolDeviations.map(item => {
      if (filter === 'site') { return get(item, 'site'); }
      if (filter === 'month') { return get(item, 'year_month'); }
    }) : [this.state.site ? get(this.state.sites.find(item => item.code === this.state.site), 'name') : 'All sites'];
  };

  getDeviationsDatasets = () => {
    const dataSet = [{ label: "Deviations", backgroundColor: "#e1e023" }];
    const protocol_deviations = get(this.state.dashboard, 'protocol_deviations', []);

    if (Array.isArray(protocol_deviations)) {
      dataSet[0].data = get(this.state.dashboard, 'protocol_deviations', []).map(item => get(item, 'count'));
    } else {
      dataSet[0].data = [protocol_deviations];
    }
    return dataSet;
  };

  getEnrolmentsStageLabels = () => {
    const { filter } = this.state;

    const enrolmentStagesRandomize = get(this.state.dashboard, 'enrolment_stages.randomize');

    return Array.isArray(enrolmentStagesRandomize) ? enrolmentStagesRandomize.map(item => {
      if (filter === 'site') { return get(item, 'site'); }
      if (filter === 'month') { return get(item, 'year_month'); }
    }) : [this.state.site ? get(this.state.sites.find(item => item.code === this.state.site), 'name') : 'All sites'];
  };

  getEnrolmentsStageDatasets = () => {
    const dataSet = [
      { label: "Randomize", backgroundColor: "#3e95cd" },
      { label: "SAE", backgroundColor: "#f00" },
      { label: "Consent", backgroundColor: "#49c850" },
      { label: "Validation", backgroundColor: "#e11eb0" },
      { label: "Deviation", backgroundColor: "#e1e023" },
      { label: "Profile", backgroundColor: "#c137bd" },
      { label: "Registry id", backgroundColor: "#41aac4" },
      { label: "Follow up 90 days", backgroundColor: "#e18e2d" },
      { label: "Death", backgroundColor: "#797677" },
    ];

    const esRandomize = get(this.state.dashboard, 'enrolment_stages.randomize');
    const esSAE = get(this.state.dashboard, 'enrolment_stages.sae');
    const esConsent = get(this.state.dashboard, 'enrolment_stages.consent');
    const esValidation = get(this.state.dashboard, 'enrolment_stages.validation');
    const esDeviation = get(this.state.dashboard, 'enrolment_stages.deviation');
    const esProfile = get(this.state.dashboard, 'enrolment_stages.profile');
    const esRegistryID = get(this.state.dashboard, 'enrolment_stages.registry_id');
    const esFollowUp90Days = get(this.state.dashboard, 'enrolment_stages.follow_up_90_days');
    const esDeath = get(this.state.dashboard, 'enrolment_stages.death');

    dataSet[0].data = Array.isArray(esRandomize) ? esRandomize.map(item => get(item, 'count')) : [esRandomize];
    dataSet[1].data = Array.isArray(esSAE) ? esSAE.map(item => get(item, 'count')) : [esSAE];
    dataSet[2].data = Array.isArray(esConsent) ? esConsent.map(item => get(item, 'count')) : [esConsent];
    dataSet[3].data = Array.isArray(esValidation) ? esValidation.map(item => get(item, 'count')) : [esValidation];
    dataSet[4].data = Array.isArray(esDeviation) ? esDeviation.map(item => get(item, 'count')) : [esDeviation];
    dataSet[5].data = Array.isArray(esProfile) ? esProfile.map(item => get(item, 'count')) : [esProfile];
    dataSet[6].data = Array.isArray(esRegistryID) ? esRegistryID.map(item => get(item, 'count')) : [esRegistryID];
    dataSet[7].data = Array.isArray(esFollowUp90Days) ? esFollowUp90Days.map(item => get(item, 'count')) : [esFollowUp90Days];
    dataSet[8].data = Array.isArray(esDeath) ? esDeath.map(item => get(item, 'count')) : [esDeath];
    return dataSet;
  };

  onDateRangeChange = (val) => {
    this.setState({ dateRange: val }, this.getDashboardData);
  };

  expandedRowRender = (record) => (<div>
    {get(record, 'subject.profile') ? <Row gutter={8}>
      <Col lg={6} md={12}>
        <h5 className="headline-default">Patient info</h5>
        <p><b>Name:</b> {get(record, 'subject.profile.first_name')} {get(record, 'subject.profile.last_name')}</p>
        <p><b>Email:</b> {get(record, 'subject.profile.email')}</p>
        <p><b>Phone:</b> {get(record, 'subject.profile.phone_number_type_1')} {get(record, 'subject.profile.phone_number_1')}</p>
        <p><b>Address:</b> {get(record, 'subject.profile.street_address')} {get(record, 'subject.profile.city')}, {get(record, 'subject.profile.postal_code')}</p>
        <p><b>Is deceased:</b> {get(record, 'subject.profile.is_deceased') ? 'Yes' : 'No'}</p>
      </Col>

      <Col lg={6} md={12}>
        <h5 className="headline-default">Other contact</h5>
        <p><b>Name:</b> {get(record, 'subject.profile.contact_1_first_name')} {get(record, 'subject.profile.contact_1_last_name')}</p>
        <p><b>Relationship:</b> {get(record, 'subject.profile.contact_1_relationship_type').replace(/_/g, ' ')}</p>
        <p><b>Email:</b> {get(record, 'subject.profile.contact_1_email')}</p>
        <p><b>Phone:</b> {get(record, 'subject.profile.contact_1_phone_number_type')} {get(record, 'subject.profile.contact_1_phone_number')}</p>
      </Col>

      {get(record, 'subject.profile.contact_2_first_name') && <Col lg={6} md={12}>
        <h5 className="headline-default">Other contact 2</h5>
        <p><b>Name:</b> {get(record, 'subject.profile.contact_2_first_name')} {get(record, 'subject.profile.contact_2_last_name')}</p>
        <p><b>Relationship:</b> {get(record, 'subject.profile.contact_2_relationship_type').replace(/_/g, ' ')}</p>
        <p><b>Email:</b> {get(record, 'subject.profile.contact_2_email')}</p>
        <p><b>Phone:</b> {get(record, 'subject.profile.contact_2_phone_number_type')} {get(record, 'subject.profile.contact_2_phone_number')}</p>
      </Col>}

      <Col lg={6} md={12}>
        <h5 className="headline-default">Family Doctor</h5>
        <p><b>Name:</b> {get(record, 'subject.profile.family_doctor_first_name')} {get(record, 'subject.profile.family_doctor_last_name')}</p>
        <p><b>Clinic:</b> {get(record, 'subject.profile.family_doctor_clinic_name')}</p>
        <p><b>Phone:</b> {get(record, 'subject.profile.family_doctor_phone_number_type')} {get(record, 'subject.profile.family_doctor_phone_number')}</p>
      </Col>
    </Row> : <div>Profile is empty</div>}
  </div>);

  customExpandIcon(props) {
    if (props.expanded) {
      return <a style={{ color: 'black' }} onClick={e => {
        props.onExpand(props.record, e);
      }}><Icon type="eye-invisible" theme="filled" className={classes.viewIcon} /></a>
    } else {
      return <a style={{ color: 'black' }} onClick={e => {
        props.onExpand(props.record, e);
      }}><Icon type="eye" theme="filled" className={classes.viewIcon} /></a>
    }
  }

  render() {
      const { dashboard, filter, sites, dateRange, followups, total, pageSize, currentPage, followup_filter, isLoadingFollowUps } = this.state;
      const progressPar = get(dashboard, 'progress_bar');

      const report30days = get(dashboard, 'summary_bi_report.follow_up_next_30_days_records', null);

      return (
          <div style={{ backgroundColor: '#FAFAFA' }} className={classes.wrapper}>
              <Helmet>
                  <title>Trial Management Portal: Dashboard</title>
              </Helmet>
              <div className={classes.parentContainer}>
                  <AdminHeader />
                  <div className={classes.content}>
                    <h4 className='headline-default'>Study progress</h4>
                    {get(progressPar, 'target_count') && (<p><b>Target count: {get(progressPar, 'target_count', 0)}</b></p>)}
                    <Row className='contentMb-24'>
                      <b>Follow up 90 days completed</b>
                      <br/>
                      <Progress percent={+(100 * get(progressPar, 'follow_up_90_days_completed_count', 0) / get(progressPar, 'target_count', 1)).toFixed(3)} strokeColor='#74B584' />

                      <b>Follow up 90 days not Completed</b>
                      <br/>
                      <Progress percent={+(100 * get(progressPar, 'follow_up_90_days_not_completed_count', 0) / get(progressPar, 'target_count', 1)).toFixed(3)} strokeColor='#F2A788' />
                    </Row>


                    {this.props.dashboardPermission && (<div>

                      {this.props.dashboardFollowUp90dPermission && <div>
                        <Row>
                          <Col md={12}>
                            <h5 className="headline-default">90-day Follow-up Summary</h5>
                            <div>
                              <HorizontalBar  data={{
                                labels: [''],
                                datasets: [
                                  { label: "Followup not due", backgroundColor: "#8b8b8d", data: [get(dashboard, 'summary_bi_report.follow_up_not_due', 0)] },
                                  { label: "Upcoming follow-ups", backgroundColor: "#e1e023", data: [get(dashboard, 'summary_bi_report.follow_up_next_30_days', 0)] },
                                  { label: "Follow-ups due", backgroundColor: "#c78538", data: [get(dashboard, 'summary_bi_report.follow_up_next_90_120_days_count', 0)] },
                                  { label: "Overdue follow-ups", backgroundColor: "#be0000", data: [get(dashboard, 'summary_bi_report.follow_up_next_over_120_days_count', 0)] },
                                  { label: "Followup completed", backgroundColor: "#2ca022", data: [get(dashboard, 'summary_bi_report.follow_up_completed', 0)] },
                                ]
                              }}
                              width={100}
                              height={300}
                              options={{ maintainAspectRatio: false, type: 'horizontalBar', scales: { xAxes: [{ticks: {beginAtZero: true, stepSize: 1 }}] } }}
                              type='horizontalBar' />
                            </div>
                          </Col>
                        </Row>

                        <h4 className='headline-default'>Upcoming follow-ups</h4>

                        <div style={{display: 'flex'}}>
                          <div style={{marginRight: '1rem'}}>
                            <label style={{ width: '300px', display: 'block' }}>
                              <b>Filter:</b>
                              <br/>
                              <Select value={followup_filter} onChange={this.setFollowUpFilter} style={{ width: '100%' }} placeholder='Select'>
                                <Option value="upcoming_not_due">Upcoming not due</Option>
                                <Option value="upcoming_next_30_days">Upcoming next 30 days</Option>
                                <Option value="follow_ups_due">Followups due</Option>
                                <Option value="follow_ups_overdue">Followups overdue</Option>
                                <Option value="follow_ups_completed">Followups completed</Option>
                              </Select>
                            </label>
                          </div>
                          <div>
                            <label style={{ width: '300px', display: 'block' }}>
                              <b>Site:</b>
                              <br/>
                              <Select
                                placeholder="Select Site ID"
                                style={{ width: '100%' }}
                                value={this.state.filterSiteId}
                                onChange={this.handleSiteFollowUpFilterChange}
                              >
                                <Option value={null}>All</Option>
                                {uniqBy(this.state.sites, 'id').map((site, index) => (
                                  <Option key={index} value={site.id}>
                                    {site.code} - {site.name}
                                  </Option>
                                ))}
                              </Select>
                            </label>
                          </div>
                          </div>
                        <br/>

                        <Table
                          className={classes.tableWrapper}
                          columns={this.columns}
                          dataSource={followups}
                          loading={isLoadingFollowUps}
                          rowKey="id"
                          onChange={this.handleTableChange}
                          expandedRowRender={this.expandedRowRender}
                          expandIcon={(props) => this.customExpandIcon(props)}
                          pagination={{
                            onShowSizeChange: (current, size) => {
                              this.setState({ pageSize: size });
                            },
                            current: currentPage,
                            total,
                            pageSize,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '25', '50', '100']
                          }}
                        />
                        <br/>
                      </div>}

                      {(this.props.dashboardReadEnrolmentBySitePermission || this.props.dashboardEnrolmentStagesPermission || this.props.dashboardReadSAEPermission || this.props.dashboardReadDeviationPermission) && <Row gutter={12}>
                        <Col md={12} lg={6}>
                          <b>Filter:</b>
                          <br/>
                          <Select value={filter} onChange={this.handleFilterChange} style={{ width: '100%' }} placeholder='Select'>
                            <Option value="site">By Site</Option>
                            <Option value="month">By Date Range</Option>
                            <Option value="">None</Option>
                          </Select>
                          <br/><br/>
                        </Col>

                        {filter === 'month' && (<Col md={12} lg={6}>
                          <b>Select date range</b>
                          <br/>
                          <RangePicker
                            value={dateRange}
                            onChange={this.onDateRangeChange}
                            format='DD MMM, YYYY'
                            allowClear={false}
                          />
                        </Col>)}

                        {(filter === 'site' || filter === 'month') && (<Col md={12} lg={6}>
                          <b>Site:</b>
                          <br/>
                          <Select onChange={this.handleSiteChange} style={{ width: '100%' }} placeholder='Select'>
                            {sites.map(site => (
                              <Option key={site.id} value={site.code}>{site.code} - {site.name}</Option>
                            ))}
                            <Option value=''>All sites</Option>
                          </Select>
                        </Col>)}
                      </Row>}

                      <Row>
                        {this.props.dashboardReadEnrolmentBySitePermission && (<Col md={12}>
                          <h5 className="headline-default">Enrolment by Site</h5>
                          <div>
                            <Bar  data={{
                              labels: this.getEnrolmentsLabels(),
                              datasets: this.getEnrolmentsDatasets()
                            }}
                            width={100}
                            height={300}
                            options={{ maintainAspectRatio: false, scales: { yAxes: [{ticks: {beginAtZero: true, stepSize: 1 }}] } }} />
                          </div>
                        </Col>)}

                        {this.props.dashboardEnrolmentStagesPermission && <Col md={12}>
                          <h5 className="headline-default">Enrolment Stage</h5>
                          <div>
                            <HorizontalBar  data={{
                              labels: this.getEnrolmentsStageLabels(),
                              datasets: this.getEnrolmentsStageDatasets()
                            }}
                            width={100}
                            height={300}
                            options={{ maintainAspectRatio: false, type: 'horizontalBar', scales: { xAxes: [{ticks: {beginAtZero: true, stepSize: 1}}] } }}
                            type='horizontalBar' />
                          </div>
                        </Col>}

                        {this.props.dashboardReadSAEPermission && (<Col md={12}>
                          <h5 className="headline-default">SAEs</h5>
                          <div>
                            <HorizontalBar  data={{
                              labels: this.getSaesLabels(),
                              datasets: this.getSaesDatasets()
                            }}
                            width={100}
                            height={300}
                            options={{ maintainAspectRatio: false, type: 'horizontalBar', scales: { xAxes: [{ticks: {beginAtZero: true, stepSize: 1}}] } }}
                            type='horizontalBar' />
                          </div>
                        </Col>)}

                        {this.props.dashboardReadDeviationPermission && (<Col md={12}>
                          <h5 className="headline-default">Deviations</h5>
                          <div>
                            <HorizontalBar  data={{
                              labels: this.getDeviationsLabels(),
                              datasets: this.getDeviationsDatasets()
                            }}
                            width={100}
                            height={300}
                            options={{ maintainAspectRatio: false, type: 'horizontalBar', scales: { xAxes: [{ticks: {beginAtZero: true, stepSize: 1 }}] } }}
                            type='horizontalBar' />
                          </div>
                        </Col>)}
                      </Row>
                    </div>)}
                  </div>
                <Footer noAdminLink />
              </div>
          </div>
      );
    }
}

const mapStateToProps = state => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  dashboardPermission: getDashboardPermission(state),
  dashboardFollowUp90dPermission: getDashboardFollowUp90dPermission(state),
  dashboardReadEnrolmentBySitePermission: getDashboardReadEnrolmentBySitePermission(state),
  dashboardEnrolmentStagesPermission: getDashboardEnrolmentStagesPermission(state),
  dashboardReadSAEPermission: getDashboardReadSAEPermission(state),
  dashboardReadDeviationPermission: getDashboardReadDeviationPermission(state),
});

const mapDispatchToProps = { logout };

export default isAuthenticated(connect(mapStateToProps, mapDispatchToProps)(AdminDashboard));



// WEBPACK FOOTER //
// ./src/containers/AdminDashboard/AdminDashboard.js